<template>
  <BaseModal :modal-name="name" max-width="max-w-2xl" @closed="onCancel">
    <p class="mb-10 text-2xl font-medium text-darkestGray">
      {{ title }}
    </p>

    <div v-if="selectedExamination">
      <BaseBoxWrapper class="mb-10 overflow-visible">
        <div class="flex flex-wrap items-center px-2.5 py-2.5 md:px-4 md:py-3">
          <div class="w-full">
            <div class="flex justify-between space-x-3">
              <span class="text-sm font-bold text-primary md:text-base">
                {{ selectedExamination.name }}
              </span>

              <span
                class="whitespace-nowrap text-sm font-medium text-primary md:text-base"
              >
                {{ formatCurrency(selectedExamination.price) }}
              </span>
            </div>
          </div>
        </div>
      </BaseBoxWrapper>

      <hr class="border-t-2 border-lightGray border-opacity-10" />
    </div>

    <div v-if="hasPatients" class="flex flex-wrap">
      <PatientsCard
        v-for="(patient, index) in eligiblePatients"
        :key="patient.id"
        :index="index"
        :patient="patient"
        :is-assigned="assignedPatients.includes(patient.id)"
        @assign="onAssignService"
        @remove="onRemoveService"
      />

      <div v-if="!isAddPatients" class="mt-5 w-full">
        <BaseLink @click="onClickAddMorePatients">
          {{ $t('cart.add_more_patients') }}</BaseLink
        >
      </div>
    </div>

    <div
      v-if="isAddPatients"
      class="mt-3 inline-flex w-full items-center space-x-3 rounded-md bg-success px-4 py-1 2xl:py-2.5"
    >
      <inline-svg
        :src="useAsset(`icons/base/info.svg`)"
        class="scale-75 2xl:scale-100"
        fill="white"
      />

      <span class="text-s font-medium text-white 2xl:text-sm">
        {{ $t('cart.other_adults_test_results') }}
      </span>
    </div>

    <div
      v-if="(!hasPatients || isAddPatients) && selectedExamination"
      class="w-full"
      :class="{ 'mt-5': isAddPatients }"
    >
      <p class="mb-10 text-2xl font-medium text-darkestGray">
        {{ $t('patients.add_patient_to_be_able_to_assign_examinations') }}
      </p>
    </div>

    <div
      v-if="!hasPatients || isAddPatients"
      :class="{ 'mt-5': isAddPatients && !selectedExamination }"
    >
      <div class="flex flex-col">
        <PatientsAddPatienSection
          ref="kidFormSection"
          age="kid"
          @added-patient="isAddPatients = true"
        />
        <PatientsAddPatienSection
          ref="adultFormSection"
          age="adult"
          @added-patient="isAddPatients = true"
        />
      </div>

      <div>
        <span class="text-sm font-medium text-darkestGray">
          {{ $t('patients.up_to_5_patients_can_be_added') }}
        </span>
      </div>
    </div>

    <div>
      <div
        class="mt-4 flex items-center justify-center space-x-8 text-sm md:justify-end"
      >
        <BaseButton
          theme="link"
          size="lg"
          class="text-primary hover:underline hover:brightness-50"
          @click="onCancel"
        >
          {{ $t('action.cancel') }}
        </BaseButton>

        <BaseButton theme="primary" size="lg" @click="onClickConfirm">
          {{ $t('action.confirm') }}
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import InlineSvg from 'vue-inline-svg'
import { useCartStore } from '~~/stores/cart'
import { useModalsStore } from '~~/stores/modals'
import { useFormatter } from '~/composables/useFormatter'
import { useChildrenHelper } from '~/composables/useChildrenHelper'
import { useAuthStore } from '~~/stores/auth'

const name = 'addPatient'
const { t } = useI18n()
const gtm = useGtm()
const { formatCurrency } = useFormatter()

const authStore = useAuthStore()
const cartStore = useCartStore()
const modalStore = useModalsStore()

const isAddPatients = ref(false)
const isRedirectingToCollectionPoints = ref(false)
const assignedPatients = ref([])

const kidFormSection = ref()
const adultFormSection = ref()

const title = computed(() => {
  return cartStore.selectedExamination
    ? t('patients.assign_selected_examination_to_a_patient')
    : t('patients.add_patient_to_be_able_to_assign_examinations')
})

const selectedExamination = computed(() => {
  return cartStore.selectedExamination
})

const hasPatients = computed(() => {
  return cartStore.patients.length > 0
})

// Only allow patients who qualify
const eligiblePatients = computed(() => {
  if (selectedExamination.value?.upsell) {
    // Gather all patient IDs eligible
    const eligibleIds = cartStore.items
      .filter((item) =>
        (item.upsell_services || []).some(
          (svc) =>
            String(svc.available_service.id) ===
            String(selectedExamination.value.id)
        )
      )
      .flatMap((item) => item.patients)
    // Return the full patient objects
    return cartStore.patients.filter((p) => eligibleIds.includes(p.id))
  }
  return cartStore.patients
})

const initialAssignedCount = ref(0)

watch(selectedExamination, () => {
  if (selectedExamination.value) {
    const currentAssigned = cartStore.getExaminationPatients(
      selectedExamination.value.id
    )
    assignedPatients.value = [...currentAssigned]
    initialAssignedCount.value = currentAssigned.length
  }
})

const onAssignService = (patient) => {
  assignedPatients.value.push(patient.id)
}

const onRemoveService = (patientId) => {
  assignedPatients.value = assignedPatients.value.filter(
    (item) => item !== patientId
  )
}

const onClickAddMorePatients = () => {
  isAddPatients.value = true
}

const resetModal = () => {
  isAddPatients.value = false
  assignedPatients.value = []
}

const onCancel = () => {
  resetModal()
  modalStore.close(name)
  cartStore.clearTmpPatients()
  cartStore.clearSelectedExamination()
  if (!isRedirectingToCollectionPoints.value) {
    cartStore.isAddingToCart = false
  } else {
    isRedirectingToCollectionPoints.value = false
  }
}

const validate = async () => {
  const [isValidKidForm, isValidAdultForm] = await Promise.all([
    kidFormSection.value.validate(),
    adultFormSection.value.validate(),
  ])

  return isValidKidForm && isValidAdultForm
}

const updateCart = () => {
  cartStore.saveTmpPatients()
  const item = {
    examination: { ...selectedExamination.value, added_by_doctor: false },
    patients: assignedPatients.value,
  }

  if (cartStore.shouldBeSelectedPoint && assignedPatients.value.length > 0) {
    cartStore.tmpItem = { ...item }
    modalStore.open('selectCollectionPointToContinue')
  } else if (
    cartStore.shouldBeSelectedCity &&
    assignedPatients.value.length > 0
  ) {
    isRedirectingToCollectionPoints.value = true
    cartStore.tmpItem = { ...item }
    modalStore.open('chooseCityModal')
  } else {
    cartStore.pushToCart(item)

    // Only trigger for add or remove, accordingly
    const examination = selectedExamination.value
    if (assignedPatients.value.length > initialAssignedCount.value) {
      gtm.trackEvent({ ecommerce: null })
      gtm.trackEvent({
        event: 'add_to_cart',
        ecommerce: {
          currency: examination.currency || 'PLN',
          value: examination.price,
          items: [
            {
              id: examination.id,
              name: examination.name,
              slug: examination.slug,
              price: examination.price,
              is_package: examination.is_package || false,
            },
          ],
          email: authStore.user?.email,
          sha256_email: authStore.user?.sha256_email,
          md5_email: authStore.user?.md5_email,
        },
      })
    } else {
      gtm.trackEvent({ ecommerce: null })
      gtm.trackEvent({
        event: 'remove_from_cart',
        ecommerce: {
          currency: examination.currency || 'PLN',
          value: examination.price,
          items: [
            {
              id: examination.id,
              name: examination.name,
              slug: examination.slug,
              price: examination.price,
              is_package: examination.is_package || false,
            },
          ],
          email: authStore.user?.email,
          sha256_email: authStore.user?.sha256_email,
          md5_email: authStore.user?.md5_email,
        },
      })
    }
    cartStore.clearSelectedExamination()
  }

  resetModal()
  modalStore.close(name)

  const patientsWithExaminations = cartStore.patients.filter((patient) => {
    const examinations = cartStore.getPatietntExaminations(patient.id)
    return examinations.length > 0
  })
  if (
    !cartStore.selectedFacility?.features?.is_children &&
    useChildrenHelper(patientsWithExaminations)
  ) {
    modalStore.open('invalidFacilityForKidModal')
  }
}

const openConfirmModal = () => {
  let title = ''
  let text = t('cart.confirm_remove_examination')

  if (cartStore.discountCode) {
    text += ` ${t('cart.confirm_remove_examination_discount')}`
  }

  modalStore.openModalCallback('confirmModal', {
    title: `<div class="text-center">${title}</div>`,
    text: `<div class="text-primary text-center">${text}</div>`,
    onConfirm: () => {
      updateCart()
      cartStore.removeDiscountCode()
    },
  })
}

const saveTomPatients = () => {
  cartStore.saveTmpPatients()
  cartStore.clearSelectedExamination()
  resetModal()
  modalStore.close(name)
  if (
    (!cartStore.selectedFacility?.features?.is_children ||
      cartStore.isHomeService) &&
    useChildrenHelper(cartStore.patients)
  ) {
    modalStore.open('invalidFacilityForKidModal')
  }
}

const checkAndUpdateCart = () => {
  const currentAssignedPatients = cartStore.getExaminationPatients(
    selectedExamination.value.id
  ).length
  const tmpPatientsIds = cartStore.tmpPatients.map((patient) => patient.id)

  assignedPatients.value = [...assignedPatients.value, ...tmpPatientsIds]

  if (assignedPatients.value.length < currentAssignedPatients) {
    openConfirmModal(assignedPatients.value)
  } else if (
    cartStore.discountCode &&
    assignedPatients.value.length != currentAssignedPatients
  ) {
    modalStore.openModalCallback('confirmModal', {
      title: `<div class="text-center">${t('cart.attention')}</div>`,
      text: `<div class="text-primary text-center">${t(
        'cart.confirm_remove_discount_code'
      )}</div>`,
      onConfirm: () => {
        updateCart()
        cartStore.removeDiscountCode()
      },
    })
  } else {
    updateCart()
  }
}

const onClickConfirm = async () => {
  const isValid = !isAddPatients.value || (await validate())

  if (isValid) {
    if (selectedExamination.value) {
      checkAndUpdateCart()
    } else {
      saveTomPatients()
    }
  }
}
</script>
